import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SelectTier = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  const tiers = [
    {
      id: "essential",
      name: "Essential",
      price: 9.99,
      coverage: "Up to $150 per event, $250 per 30 days",
      features: ["Basic protection", "Email support", "Same-day review"]
    },
    {
      id: "standard",
      name: "Standard",
      price: 19.99,
      coverage: "Up to $300 per event, $500 per 30 days",
      features: ["Enhanced protection", "Priority support", "Fast-track review", "Flexible repayment"],
      featured: true
    },
    {
      id: "plus",
      name: "Plus",
      price: 39.99,
      coverage: "Up to $600 per event, $1000 per 30 days",
      features: ["Maximum protection", "24/7 support", "Instant review", "Custom repayment plans"]
    }
  ];

  const handleSelectTier = async (tier) => {
    setSelectedTier(tier.id);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/subscription/create-checkout`, 
        { 
          tier: tier.id,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoading(false);
      setSelectedTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-background py-12 px-6">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-testid="select-tier-title">
            Choose Your Protection Level
          </h1>
          <p className="text-xl text-muted-foreground">
            All plans include human review, flexible repayment, and member-friendly terms.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier) => (
            <div 
              key={tier.id}
              data-testid={`tier-card-${tier.id}`}
              className={`card-pricing ${tier.featured ? 'ring-2 ring-primary scale-105' : ''}`}
            >
              {tier.featured && (
                <div className="absolute top-0 right-0 bg-accent text-accent-foreground px-4 py-1 text-sm font-medium rounded-bl-lg">
                  Most Popular
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-heading font-bold mb-2">{tier.name}</h3>
                <div className="text-5xl font-heading font-bold text-primary mb-2">
                  ${tier.price}<span className="text-2xl text-muted-foreground">/mo</span>
                </div>
                <p className="text-sm text-muted-foreground">{tier.coverage}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                data-testid={`subscribe-${tier.id}-btn`}
                onClick={() => handleSelectTier(tier)}
                className="btn-primary w-full"
                disabled={loading && selectedTier === tier.id}
              >
                {loading && selectedTier === tier.id ? 'Processing...' : `Subscribe to ${tier.name}`}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Payments are securely processed by Stripe. You can cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectTier;
